import { useEffect, useState } from 'react'

import { useCommercialStructure } from 'commons/customHooks'
import { getLastDayOfMonth } from 'commons/utils/date'
import { getUserEmail } from 'commons/utils/user'
import {
  Alert,
  BatchSearch,
  DatePicker,
  Icon,
  LoadingModal,
  Page,
  Select,
  SlimButton,
} from 'components'
import {
  allFactoriesFilter,
  allSectorOptions,
  Company,
  companies,
  organizationOptions,
  productionTypeOptions,
  TypesValidations,
} from 'constants/enums'
import dayjs from 'dayjs'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { postStockReport } from 'services/stock'
import type { ExportStockReport } from 'services/stock/interface'

import * as S from './styles'

export const StockReport = () => {
  const { getMarkets } = useCommercialStructure()

  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState<boolean>()

  const markets = getMarkets()
  const marketOptions = markets.map((market) => ({
    title: market.description,
    value: market.id,
  }))

  const defaultFields: {
    empresa: string
    organizacao: string
    canal: string
    setor: string
    centro: string
    foraDeLinha: string
    dataFinalFaturamento: string
    materiais: string
    emailUsuario: string
  } = {
    canal: '',
    centro: '',
    dataFinalFaturamento: '',
    emailUsuario: '',
    empresa: Company.ALPA,
    foraDeLinha: '',
    materiais: '',
    organizacao: '',
    setor: '',
  }

  const empresa = [
    {
      title: companies[Company.ALPA].title,
      value: companies[Company.ALPA].value,
    },
  ]

  const methods = useForm({
    defaultValues: defaultFields,
  })

  const { handleSubmit, watch, getValues } = methods

  const watchFields = watch()
  const watchRequiredFields = watch([
    'organizacao',
    'canal',
    'setor',
    'centro',
    'foraDeLinha',
    'dataFinalFaturamento',
  ])
  const isDisabled = watchRequiredFields.some((field) => !field)

  useEffect(() => {
    if (hasError !== undefined) {
      setHasError(undefined)
    }
  }, [JSON.stringify(watchFields)])

  const requestStockReport = async () => {
    const {
      empresa,
      organizacao,
      canal,
      setor,
      centro,
      foraDeLinha,
      dataFinalFaturamento,
      materiais,
    } = getValues()

    const requestBody = {
      Canal: canal,
      Centro: centro,
      DataFinalFaturamento: getLastDayOfMonth(
        dataFinalFaturamento,
        'YYYY-MM-DDTHH:mm:ss',
      ) as unknown as string,
      EmailUsuario: getUserEmail(),
      Empresa: empresa,
      ForaDeLinha: foraDeLinha == TypesValidations.YES,
      Materiais: materiais ? materiais.split(',').filter(Boolean) : [],
      Organizacao: organizacao,
      Setor: setor,
    }

    try {
      setLoading(true)
      await postStockReport(requestBody as ExportStockReport)

      setHasError(false)
    } catch (error) {
      setHasError(true)
    } finally {
      setLoading(false)
    }
  }

  const renderAlert = () => {
    if (hasError === undefined) {
      return null
    }

    const kind = hasError ? 'error' : 'success'
    const title = hasError
      ? 'Ocorreu um erro ao tentar registrar sua solicitação.'
      : 'Solicitação registrada com sucesso!'
    const message = hasError
      ? 'Tente realizar o registro da sua solicitação novamente em alguns minutos.'
      : 'A planilha solicitada será enviada para o seu email cadastrado.'

    return <Alert fullWidth kind={kind} message={message} title={title} />
  }

  return (
    <>
      {loading && <LoadingModal />}
      <Page fullwidth>
        <FormProvider {...methods}>
          <S.Form>
            <S.Title>Solicitação de relatório de estoque / reserva</S.Title>
            <S.FieldsContainer>
              <S.FieldWrapper mediaTemplateColumns="1fr 1fr" templateColumns="1fr 1fr 1fr 1fr">
                <S.FieldItemWrapper>
                  <Controller
                    defaultValue={Company.ALPA}
                    name="empresa"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        disabled={true}
                        id="empresa"
                        label="Empresa"
                        name={name}
                        onChange={(e) => onChange(e)}
                        options={empresa}
                        value={value}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
                <S.FieldItemWrapper>
                  <Controller
                    name="organizacao"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        id="organizacao"
                        label="Organização"
                        name={name}
                        onChange={(e) => onChange(e)}
                        options={organizationOptions}
                        value={value}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
                <S.FieldItemWrapper>
                  <Controller
                    name="canal"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        id="canal"
                        label="Mercado"
                        name={name}
                        onChange={(e) => onChange(e)}
                        options={marketOptions}
                        value={value}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
                <S.FieldItemWrapper>
                  <Controller
                    name="setor"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        id="setor"
                        label="Setor"
                        name={name}
                        onChange={(e) => onChange(e)}
                        options={allSectorOptions}
                        value={value}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
              </S.FieldWrapper>
              <S.FieldWrapper mediaTemplateColumns="1fr 1fr" templateColumns="1fr 1fr 1fr 1fr">
                <S.FieldItemWrapper>
                  <Controller
                    name="centro"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        id="centro"
                        label="Fábrica"
                        name={name}
                        onChange={(e) => onChange(e)}
                        options={allFactoriesFilter}
                        value={value}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
                <S.FieldItemWrapper>
                  <Controller
                    name="foraDeLinha"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        id="foraDeLinha"
                        label="Status do material"
                        name={name}
                        onChange={(e) => onChange(e)}
                        options={productionTypeOptions}
                        value={value}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
                <S.FieldItemWrapper>
                  <S.LabelFilter>Data de Faturamento</S.LabelFilter>
                  <Controller
                    name="dataFinalFaturamento"
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        disableMaskedInput={true}
                        error={fieldState.error?.message}
                        height={30}
                        inputFormat="MM/YYYY"
                        maxDate={dayjs().add(6, 'month') as unknown as string}
                        minDate={dayjs() as unknown as string} // TODO: replace direct dayjs() usage with utils
                        placeholder="mm/aaaa"
                        readOnlyInput
                        views={['year', 'month']}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
                <S.FieldItemWrapper>
                  <S.LabelFilter>Materiais específicos</S.LabelFilter>
                  <Controller
                    name="materiais"
                    render={({ field }) => (
                      <BatchSearch
                        height={30}
                        onChange={(val) => field.onChange(val)}
                        placeHolder="Buscar por códigos (Ex: 4000029)"
                        value={field.value || ''}
                      />
                    )}
                  />
                </S.FieldItemWrapper>
              </S.FieldWrapper>
            </S.FieldsContainer>

            <S.ActionsContainer>
              <SlimButton
                content="Solicitar"
                disabled={isDisabled || loading}
                icon={<Icon icon="search" size="15" />}
                id="request-button"
                loading={loading}
                onClick={handleSubmit(requestStockReport)}
                type="button"
                variant="primary"
              />
            </S.ActionsContainer>
          </S.Form>
        </FormProvider>

        {renderAlert()}
      </Page>
    </>
  )
}
